import { Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef, useContext } from "react";
import { useAppKitAccount, useAppKit, useDisconnect } from '@reown/appkit/react';
import ListTag from '../../../components/ListTag';
import SecNavItem from './SecNavItem';
import SecDropdown from './SecDropdown';
import { AuthContext } from '../../../lib/auth';
import { get_permissions } from '../../../utils';
import { useI18n } from '../../../i18n/context';
import { CHAIN_ARTICLES } from '../../../data/chains';

/**
 * Chains shown in the Chains dropdown. Order matches `CHAIN_ARTICLES` in
 * `@/data/chains`, and each `slug` has a matching route at /chains/<slug>.
 */
const CHAIN_LINKS = CHAIN_ARTICLES.map((article) => ({
  slug: article.slug,
  label: article.name,
}));

/** @module layouts/Head/Navbar */
function WalletConnect({ t }: { t?: any }) {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const { isConnected, address } = useAppKitAccount();
  const { open } = useAppKit();
  const { disconnect } = useDisconnect();
  const connectionIntentRef = useRef(false);
  const isConnectedRef = useRef(isConnected);

  useEffect(() => { setMounted(true); }, []);
  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);
  useEffect(() => {
    if (isConnectedRef.current && connectionIntentRef.current) {
      connectionIntentRef.current = false;
      navigate({ to: '/signin' } as any);
    }
  }, [isConnected, navigate]);

  const handleClick = async () => {
    if (isConnected) {
      await disconnect();
    } else {
      connectionIntentRef.current = true;
      try {
        await open();
      } catch (err) {
        console.error('Wallet open error:', err);
        connectionIntentRef.current = false;
      }
    }
  };

  const formatAddress = (addr: string | undefined) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  return (
    <button className="pixel-btn btn-sm" onClick={handleClick}>
      {mounted ? isConnected ? formatAddress(address) : t('nav.connectWallet') : t('nav.connectWallet')}
    </button>
  );
}

function Navbar() {
  const [mounted, setMounted] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const authContext = useContext(AuthContext);
  const { isAuthenticated, user } = authContext ?? { isAuthenticated: false, user: null };
  const { t } = useI18n();
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  // Auto-hide navbar on scroll down; show on scroll up
  useEffect(() => {
    if (!mounted) return;
    let lastScroll = 0;
    const handleScroll = () => {
      const current = window.scrollY || window.pageYOffset;
      const header = navRef.current?.closest('header');
      if (!header) return;
      if (current > lastScroll && current > 60) {
        header.classList.add('hide-on-scroll');
      } else {
        header.classList.remove('hide-on-scroll');
      }
      lastScroll = current;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    if (isAuthenticated && user?.account?.id) {
      let id = user.account.id;
      if (typeof id === 'string') {
        id = id.split(':')[0];
      }
      setAvatarUrl(`https://cdn.stealthisnft.xyz/u/${id}/profile/avatar.jpg`);
      setShowProfile(true);
      return;
    }
    setShowProfile(false);
  }, [mounted, isAuthenticated, user]);

  const closeDropdowns = () => {
    navRef.current?.querySelectorAll('.nav-dropdown.open').forEach(el => el.classList.remove('open'));
  };

  const closeMobileMenu = () => {
    document.getElementById('mainNavbar')?.classList.remove('show');
    closeDropdowns();
  };

  const handleToggleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const btn = e.currentTarget;
    const dropdown = btn.closest('.nav-dropdown');
    if (!dropdown) return;
    const isOpen = dropdown.classList.contains('open');
    closeDropdowns();
    if (!isOpen) {
      dropdown.classList.add('open');
    }
  };

  useEffect(() => {
    if (!mounted) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (navRef.current && !navRef.current.contains(target)) {
        closeDropdowns();
      }
      // Mobile: a click anywhere outside the open panel collapses it.
      // Ignore the burger itself — its own handler toggles the panel.
      const panel = document.getElementById('mainNavbar');
      if (
        panel?.classList.contains('show') &&
        !panel.contains(target) &&
        !(target as HTMLElement).closest?.('.navbar-toggler')
      ) {
        closeMobileMenu();
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mounted]);

  // Mobile: collapse the open panel whenever a nav item or sub-item is chosen
  // (any link/button except the dropdown toggles that just open a submenu).
  useEffect(() => {
    if (!mounted) return;
    const panel = document.getElementById('mainNavbar');
    if (!panel) return;
    const handlePanelClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!panel.classList.contains('show')) return;
      if (target.closest('.nav-dropdown-toggle')) return;
      if (target.closest('a, button')) closeMobileMenu();
    };
    panel.addEventListener('click', handlePanelClick);
    return () => panel.removeEventListener('click', handlePanelClick);
  }, [mounted]);

  return (
    <header>
      <div ref={navRef} style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '0 5vw', boxSizing: 'border-box' }}>
        {/* Brand Logo */}
          <Link className="navbar-brand" to="/">
            STN-MP
          </Link>

        {/* Mobile Toggler */}
        <div style={{ marginLeft: 'auto' }}>
          <button
            className="navbar-toggler"
            type="button"
            onClick={() => document.getElementById('mainNavbar')?.classList.toggle('show')}
            aria-controls="mainNavbar"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-bar"></span>
            <span className="navbar-toggler-bar"></span>
            <span className="navbar-toggler-bar"></span>
          </button>
        </div>

        {/* Collapsible Navigation — desktop inline; mobile hidden by Bootstrap */}
        <div className="navbar-collapse nav-dropdown-mobile" id="mainNavbar">
          <ul className="pixel-nav">
            {showProfile && (
              <li>
                <Link className="pixel-nav-link" to="/profile" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={(e) => {
                      const fb = "https://cdn.stealthisnft.xyz/u/0/profile/avatar.jpg";
                      if (e.currentTarget.src === fb) return; // prevent loop
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = fb;
                    }}
                  />
                  <span>{t('nav.profile')}</span>
                </Link>
              </li>
            )}

            {/* Explore Dropdown */}
            <li className="nav-dropdown">
              <button className="nav-dropdown-toggle" aria-expanded="false" onClick={handleToggleClick}>
                {t('nav.explore')}
              </button>
              <div className="nav-dropdown-menu">
                <Link className="nav-dropdown-item" to="/explore?sort=trending" onClick={closeDropdowns}>{t('nav.exploreTrending')}</Link>
                <Link className="nav-dropdown-item" to="/explore?sort=new" onClick={closeDropdowns}>{t('nav.exploreNew')}</Link>
                <Link className="nav-dropdown-item" to="/explore?category=nfts" onClick={closeDropdowns}>{t('nav.exploreNfts')}</Link>
                <Link className="nav-dropdown-item" to="/explore?category=photography" onClick={closeDropdowns}>{t('nav.explorePhotography')}</Link>
                <Link className="nav-dropdown-item" to="/explore?category=art" onClick={closeDropdowns}>{t('nav.exploreArt')}</Link>
                <a className="nav-dropdown-item" href="https://pixel.stealthisnft.xyz" target="_blank" rel="noopener noreferrer" onClick={closeDropdowns}>Pixel Editor</a>
                <Link className="nav-dropdown-item" to="/physical-goods" onClick={closeDropdowns}>{t('nav.physicalGoods')}</Link>
                <Link className="nav-dropdown-item" to="/hall-of-fame" onClick={closeDropdowns}>{t('nav.hallOfFame')}</Link>
                <Link className="nav-dropdown-item" to="/home" onClick={closeDropdowns}>{t('nav.currentListings')}</Link>
              </div>
            </li>

            {/* Collections */}
            <li>
              <Link className="pixel-nav-link" to="/collections">{t('nav.collections')}</Link>
            </li>

            {/* Physical Goods */}
            <li>
              <Link className="pixel-nav-link" to="/physical-goods">{t('nav.physicalGoods')}</Link>
            </li>

            {/* Chains Dropdown */}
            <li className="nav-dropdown">
              <button className="nav-dropdown-toggle" aria-expanded="false" onClick={handleToggleClick}>
                {t('nav.chains')}
              </button>
              <div className="nav-dropdown-menu">
                {CHAIN_LINKS.map((chain) => (
                  <Link
                    key={chain.slug}
                    className="nav-dropdown-item"
                    to={`/chains/${chain.slug}` as any}
                    onClick={closeDropdowns}
                  >
                    {chain.label}
                  </Link>
                ))}

                {/* Community links, visually separated from the chains above. */}
                <div className="nav-dropdown-separator" role="separator" />
                <Link className="nav-dropdown-item" to="/authors" onClick={closeDropdowns}>{t('nav.members')}</Link>
                <Link className="nav-dropdown-item" to="/mint" onClick={closeDropdowns}>{t('nav.dapps')}</Link>
              </div>
            </li>
          </ul>

          {/* Right side: Actions */}
          <ul className="pixel-nav pixel-nav-right">
            {/* Create CTA */}
            <li>
              <Link className="pixel-btn btn-sm" to="/manage" style={{ textDecoration: 'none', color: '#fff' }}>
                {t('nav.create')}
              </Link>
            </li>

            {/* Admin Dropdown */}
            {mounted && get_permissions?.('admin') && (
              <li className="nav-dropdown">
                <button className="nav-dropdown-toggle" style={{ color: '#f6c96b' }} onClick={handleToggleClick}>
                  <i className="fas fa-cogs" style={{ marginRight: '4px' }}></i>
                  {t('nav.admin')}
                </button>
                <div className="nav-dropdown-menu">
                  <Link className="nav-dropdown-item" to="/admin/minting-dapps">
                    <i className="fas fa-hammer" style={{ marginRight: '6px' }}></i>
                    {t('nav.mintingDapps')}
                  </Link>
                </div>
              </li>
            )}

            {/* Connect Wallet */}
            <li>
              {mounted ? <WalletConnect t={t} /> : (
                <button className="pixel-btn btn-sm">{t('nav.connectWallet')}</button>
              )}
            </li>

            {/* Sign In/Out */}
            <SecNavItem />

            {/* Notifications Dropdown */}
            <li style={{ display: 'none' }}>
              <ListTag />
              <SecDropdown />
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
